import { useEffect, useState } from 'react';
import {
  faGear,
  faObjectsAlignBottom,
  faObjectsAlignCenterHorizontal,
  faObjectsAlignCenterVertical,
  faObjectsAlignLeft,
  faObjectsAlignRight,
  faObjectsAlignTop,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { ImageSelectorModal } from '@/components/editor/image-selector-modal';
import {
  ALIGNMENT_BUTTON_CLASS,
  ALIGNMENT_TOOLTIP_CONTENT_PROPS,
  OptionButtonGroup,
  type OptionButtonOption,
  SettingRow,
} from '@/components/page-editor/settings-panel/fields';
import { FloatWrapControl } from '@/components/page-editor/settings-panel/float-wrap-control';
import { SettingsPopoverPanel } from '@/components/page-editor/settings-panel/panel';
import {
  type BackgroundDraft,
  BackgroundSettingsPanel,
} from '@/components/shared/background-settings/background-settings-panel';
import { ButtonGroup } from '@/components/ui/button-group';
import { Icon } from '@/components/ui/icon';
import { IconTooltipButton } from '@/components/ui/icon-tooltip-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type {
  GroupAlign,
  GroupChildFloat,
  GroupDirection,
  GroupGap,
  GroupJustify,
} from '@/lib/blocks/layout/group-layout';
import { getBlockFloat, resolveGroupLayoutSettings } from '@/lib/blocks/layout/group-layout';
import { isEditorPortalTarget } from '@/lib/editor-portal-targets';
import { scheduleScrollToPageEditorBlock } from '@/lib/page-editor/scroll-to-block';
import { cn } from '@/lib/utils';
import { getBlockUiKey, type PageBlock } from '@/types/block';
import type { SelectedAssetData } from '@/types/image';
import { BlockActionButton } from './block-action-button';

interface GroupSettingsPopoverProps {
  block: PageBlock;
  onUpdateGroupProps: (blockId: string, patch: Record<string, unknown>) => void;
  isVisible?: boolean;
  showLabel?: boolean;
  className?: string;
  disabled?: boolean;
  showFloatOption?: boolean;
  sectionId?: string;
}

type GroupSettingsTab = 'background' | 'settings';

const directionOptions: OptionButtonOption<GroupDirection>[] = [
  { value: 'horizontal', label: 'Horizontal' },
  { value: 'vertical', label: 'Vertical' },
];

const gapOptions: OptionButtonOption<GroupGap>[] = [
  { value: 'sm', label: 'S' },
  { value: 'md', label: 'M' },
  { value: 'lg', label: 'L' },
];

type SelectableAlign = Exclude<GroupAlign, 'stretch'>;
type SelectableJustify = Exclude<GroupJustify, 'between'>;
type AlignmentValue = SelectableAlign | SelectableJustify;

const alignOptions: SelectableAlign[] = ['start', 'center', 'end'];
const justifyOptions: SelectableJustify[] = ['start', 'center', 'end'];

const alignmentLabelMap: Record<AlignmentValue, string> = {
  start: 'Start',
  center: 'Center',
  end: 'End',
};

const POPOVER_MAX_HEIGHT = 'max-h-[min(82vh,760px)]';

const VERTICAL_ALIGNMENT_ICONS: Record<AlignmentValue, typeof faObjectsAlignTop> = {
  start: faObjectsAlignTop,
  center: faObjectsAlignCenterVertical,
  end: faObjectsAlignBottom,
};

const HORIZONTAL_ALIGNMENT_ICONS: Record<AlignmentValue, typeof faObjectsAlignLeft> = {
  start: faObjectsAlignLeft,
  center: faObjectsAlignCenterHorizontal,
  end: faObjectsAlignRight,
};

function AlignmentIcon({ value, vertical }: { value: AlignmentValue; vertical?: boolean }) {
  const icon = vertical ? VERTICAL_ALIGNMENT_ICONS[value] : HORIZONTAL_ALIGNMENT_ICONS[value];
  return <Icon icon={icon} className="size-4" />;
}

function renderAlignmentButton(
  option: AlignmentValue,
  activeValue: string,
  onClick: () => void,
  disabled: boolean,
  vertical?: boolean,
) {
  return (
    <IconTooltipButton
      key={option}
      tooltip={alignmentLabelMap[option]}
      tooltipContentProps={ALIGNMENT_TOOLTIP_CONTENT_PROPS}
      variant={activeValue === option ? 'solid' : 'outline'}
      colorScheme="secondary"
      size="icon"
      className={ALIGNMENT_BUTTON_CLASS}
      onClick={onClick}
      disabled={disabled}
    >
      <AlignmentIcon value={option} vertical={vertical} />
    </IconTooltipButton>
  );
}

function buildBackgroundDraft(block: PageBlock): BackgroundDraft {
  const layout = resolveGroupLayoutSettings(block);
  const explicitType = layout.backgroundType;
  const inferredType: BackgroundDraft['backgroundType'] = explicitType
    ? explicitType
    : layout.backgroundImage
      ? 'image'
      : layout.backgroundVideo
        ? 'video'
        : 'color';
  return {
    backgroundType: inferredType,
    backgroundImage: layout.backgroundImage,
    backgroundVideo: layout.backgroundVideo,
    backgroundColor: layout.backgroundColor ?? '',
    backgroundOverlay: layout.backgroundOverlay,
    backgroundOverlayColor: layout.backgroundOverlayColor,
    backgroundFixed: layout.backgroundFixed,
    backgroundFocusX: layout.backgroundFocusX,
    backgroundFocusY: layout.backgroundFocusY,
  };
}

export function GroupSettingsPopover({
  block,
  onUpdateGroupProps,
  isVisible = false,
  showLabel = false,
  className,
  disabled = false,
  showFloatOption = false,
  sectionId,
}: GroupSettingsPopoverProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<GroupSettingsTab>('background');
  const [isMediaSelectorOpen, setIsMediaSelectorOpen] = useState(false);
  const groupLayout = resolveGroupLayoutSettings(block);
  const currentFloat = getBlockFloat(block);
  const isHorizontalGroup = groupLayout.direction === 'horizontal';
  const verticalValue: string = isHorizontalGroup ? groupLayout.align : groupLayout.justify;
  const horizontalValue: string = isHorizontalGroup ? groupLayout.justify : groupLayout.align;
  const verticalOptions: AlignmentValue[] = isHorizontalGroup ? alignOptions : justifyOptions;
  const horizontalOptions: AlignmentValue[] = isHorizontalGroup ? justifyOptions : alignOptions;

  useEffect(() => {
    if (open) setActiveTab('background');
  }, [open]);

  const backgroundDraft = buildBackgroundDraft(block);

  const updateGroupProps = (patch: Record<string, unknown>) => {
    onUpdateGroupProps(block.id, patch);
  };
  const blockUiKey = getBlockUiKey(block);
  const handleFloatChange = (float: GroupChildFloat | null) => {
    if (float === currentFloat) return;
    updateGroupProps({ float });
    scheduleScrollToPageEditorBlock(blockUiKey);
  };
  const shouldShowLabel = showLabel || open;

  const handleVerticalAlignmentChange = (value: AlignmentValue) => {
    if (isHorizontalGroup) {
      updateGroupProps({ align: value as GroupAlign });
      return;
    }

    updateGroupProps({ justify: value as GroupJustify });
  };

  const handleHorizontalAlignmentChange = (value: AlignmentValue) => {
    if (isHorizontalGroup) {
      updateGroupProps({ justify: value as GroupJustify });
      return;
    }

    updateGroupProps({ align: value as GroupAlign });
  };

  const handleBackgroundChange = (patch: Partial<BackgroundDraft>) => {
    updateGroupProps(patch);
  };

  const handleSelectMedia = (data: SelectedAssetData) => {
    if (backgroundDraft.backgroundType === 'video') {
      updateGroupProps({
        backgroundVideo: {
          assetId: data.assetId,
          url: data.url,
        },
      });
    } else {
      updateGroupProps({
        backgroundImage: {
          assetId: data.assetId,
          url: data.url,
          alt: data.altText,
          folderId: data.folderId,
          variants: data.variants,
        },
      });
    }
    setIsMediaSelectorOpen(false);
  };

  const handleRemoveMedia = () => {
    if (backgroundDraft.backgroundType === 'video') {
      updateGroupProps({ backgroundVideo: null });
    } else {
      updateGroupProps({ backgroundImage: null });
    }
  };

  const currentMediaAssetId =
    backgroundDraft.backgroundType === 'video'
      ? backgroundDraft.backgroundVideo?.assetId
      : backgroundDraft.backgroundImage?.assetId;
  const currentMediaFolderId =
    backgroundDraft.backgroundType === 'video'
      ? undefined
      : backgroundDraft.backgroundImage?.folderId;
  const currentMediaUrl =
    backgroundDraft.backgroundType !== 'video' && !currentMediaAssetId
      ? backgroundDraft.backgroundImage?.url
      : undefined;

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && isMediaSelectorOpen) return;
          setOpen(nextOpen);
        }}
      >
        <PopoverTrigger asChild>
          <BlockActionButton
            icon={<Icon icon={faGear} className="size-3.5" />}
            label="Settings"
            srLabel="Group settings"
            showLabel={shouldShowLabel}
            isVisible={isVisible}
            disabled={disabled}
            className={className}
          />
        </PopoverTrigger>

        <PopoverContent
          side="top"
          align="end"
          sideOffset={6}
          hideWhenDetached
          updatePositionStrategy={showFloatOption ? 'always' : 'optimized'}
          onInteractOutside={(event) => {
            if (isMediaSelectorOpen || isEditorPortalTarget(event.target)) {
              event.preventDefault();
            }
          }}
          className={cn(
            'z-40 w-[27rem] overflow-hidden rounded-md border bg-background p-0 shadow-2xl',
            POPOVER_MAX_HEIGHT,
          )}
          onPointerDown={(event) => {
            // Stops the click reaching the drag handling underneath, which would
            // otherwise read a click in this popover as the start of dragging the
            // group around the page.
            event.stopPropagation();
          }}
        >
          <SettingsPopoverPanel
            title="Group Settings"
            onRequestClose={() => setOpen(false)}
            className={POPOVER_MAX_HEIGHT}
          >
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as GroupSettingsTab)}
              className="md:gap-2"
            >
              <TabsList variant="underline" fullWidth>
                <TabsTrigger value="background">Background</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>

              <TabsContent value="background">
                <BackgroundSettingsPanel
                  draft={backgroundDraft}
                  onChange={handleBackgroundChange}
                  onOpenMediaSelector={() => setIsMediaSelectorOpen(true)}
                  onRemoveMedia={handleRemoveMedia}
                  colorHelperText="Set a custom background color for this group (overrides site-wide colors)."
                />
              </TabsContent>

              <TabsContent value="settings">
                <div className="space-y-3 pt-1">
                  <SettingRow
                    title="Direction"
                    description="Arrange children vertically or horizontally."
                  >
                    <OptionButtonGroup
                      options={directionOptions}
                      value={groupLayout.direction}
                      onChange={(value) => updateGroupProps({ direction: value })}
                      disabled={disabled}
                      buttonClassName="px-3 text-xs font-semibold"
                    />
                  </SettingRow>

                  <SettingRow
                    title="Spacing"
                    description="Set spacing between cards in this group."
                  >
                    <OptionButtonGroup
                      options={gapOptions}
                      value={groupLayout.gap}
                      onChange={(value) => updateGroupProps({ gap: value })}
                      disabled={disabled}
                      buttonClassName="px-3 text-xs font-semibold"
                    />
                  </SettingRow>

                  <SettingRow
                    title="Vertical Alignment"
                    description="Align items vertically in this group."
                  >
                    <ButtonGroup className="w-full">
                      {verticalOptions.map((option) =>
                        renderAlignmentButton(
                          option,
                          verticalValue,
                          () => handleVerticalAlignmentChange(option),
                          disabled,
                          true,
                        ),
                      )}
                    </ButtonGroup>
                  </SettingRow>

                  <SettingRow
                    title="Horizontal Alignment"
                    description="Align items horizontally in this group."
                  >
                    <ButtonGroup className="w-full">
                      {horizontalOptions.map((option) =>
                        renderAlignmentButton(
                          option,
                          horizontalValue,
                          () => handleHorizontalAlignmentChange(option),
                          disabled,
                        ),
                      )}
                    </ButtonGroup>
                  </SettingRow>

                  {showFloatOption && (
                    <FloatWrapControl
                      layout="row"
                      value={currentFloat}
                      onChange={handleFloatChange}
                      description="Float this group so surrounding content in the parent group wraps around it."
                      disabled={disabled}
                      buttonClassName="px-3 text-xs font-semibold"
                    />
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </SettingsPopoverPanel>
        </PopoverContent>
      </Popover>

      <ImageSelectorModal
        isOpen={isMediaSelectorOpen}
        onClose={() => setIsMediaSelectorOpen(false)}
        onSelectImage={handleSelectMedia}
        fileTypes={backgroundDraft.backgroundType === 'video' ? 'videos' : 'images'}
        selectedAssetId={currentMediaAssetId}
        selectedFolderId={currentMediaFolderId}
        initialImageUrl={currentMediaUrl}
        sectionId={sectionId}
        enableCreateImage={backgroundDraft.backgroundType !== 'video'}
      />
    </>
  );
}
